import { useState, useRef } from "react";
import PLAY_SVG from "assets/images/play.svg";
import STOP_SVG from "assets/images/stop.svg";
import { sentence } from "shared/types/Report";
import {
    ContentShell, Panel, PanelHeader, PanelTitle, CountBadge, EmptyBadge,
    EmptyState, EmptyTitle, EmptyText, ViewerTable, ViewerHeader, ViewerBody, ViewerRow, ViewerCol,
    ViewerIndex, ViewerStrong, ViewerThumb, ViewerAudioBox, ViewerPlayBtn
} from "../homeworkUi";

interface SentenceTableProps {
    list: sentence[]
}

const SentenceTableViewer = ({ list }: SentenceTableProps) => {
    const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
    const [isPlayingList, setIsPlayingList] = useState<{[key: number]: boolean}>({});

    const handlePlayPause = (index: number) => {
        const audioRef = audioRefs.current[index];
        if (audioRef) {
            if (isPlayingList[index]) {
                audioRef.pause();
            } else {
                audioRef.play();
            }
            setIsPlayingList(prev => ({
                ...prev,
                [index]: !prev[index]
            }));
        }
    };

    const renderAudioPreview = (item: sentence, index: number) => {
        if (item.audio) {
            const audioUrl = typeof item.audio === "string" ? item.audio : "";
            if (!audioUrl) return <EmptyBadge>없음</EmptyBadge>;
            return (
                <ViewerAudioBox>
                    <audio ref={(el) => (audioRefs.current[index] = el)} src={audioUrl} />
                    <ViewerPlayBtn
                        src={isPlayingList[index] ? STOP_SVG : PLAY_SVG}
                        alt="play"
                        onClick={() => handlePlayPause(index)}
                    />
                </ViewerAudioBox>
            );
        }
        return <EmptyBadge>없음</EmptyBadge>;
    };

    const renderImagePreview = (item: sentence) => {
        if (item.image_preview) {
            return <ViewerThumb src={item.image_preview} alt="preview" />
        } else if (item.image) {
            const imageUrl = typeof item.image === 'string' ? item.image : "";
            if (!imageUrl) return <EmptyBadge>없음</EmptyBadge>;
            return <ViewerThumb src={imageUrl} alt="preview" />
        }
        return <EmptyBadge>없음</EmptyBadge>;
    }

    if(!list.length){
        return (
            <ContentShell>
                <Panel>
                    <EmptyState>
                        <EmptyTitle>문장 콘텐츠가 없습니다</EmptyTitle>
                        <EmptyText>이 과제에 등록된 문장이 없거나 조회 권한이 없습니다.</EmptyText>
                    </EmptyState>
                </Panel>
            </ContentShell>
        )
    }

    return (
        <ContentShell>
            <Panel>
                <PanelHeader>
                    <PanelTitle>문장</PanelTitle>
                    <CountBadge>{list.length}개</CountBadge>
                </PanelHeader>
                <ViewerTable>
                    <ViewerHeader>
                        <ViewerCol style={{ width: "6%" }}>번호</ViewerCol>
                        <ViewerCol style={{ width: "36%" }}>문장</ViewerCol>
                        <ViewerCol style={{ width: "30%" }}>해석</ViewerCol>
                        <ViewerCol style={{ width: "14%" }}>음원</ViewerCol>
                        <ViewerCol style={{ width: "14%" }}>이미지</ViewerCol>
                    </ViewerHeader>
                    <ViewerBody>
                        {list.map((item, index) => (
                            <ViewerRow key={index + 1}>
                                <ViewerCol style={{ width: "6%" }}><ViewerIndex>{index + 1}</ViewerIndex></ViewerCol>
                                <ViewerCol style={{ width: "36%" }}><ViewerStrong>{item.sentence}</ViewerStrong></ViewerCol>
                                <ViewerCol style={{ width: "30%" }}>{item.kor || <EmptyBadge>없음</EmptyBadge>}</ViewerCol>
                                <ViewerCol style={{ width: "14%" }}>{renderAudioPreview(item, index)}</ViewerCol>
                                <ViewerCol style={{ width: "14%" }}>{renderImagePreview(item)}</ViewerCol>
                            </ViewerRow>
                        ))}
                    </ViewerBody>
                </ViewerTable>
            </Panel>
        </ContentShell>
    )
}

export default SentenceTableViewer;
